/**
 * Marque de l'organisme de l'adresse (04/11/2026, multi-organisme) : app.skills4mation.com affiche Skills4mation,
 * app.formatrix.fr affiche Formatrix. Le logo est celui déposé par l'organisme (Organisme → Identité) — jamais un logo
 * fabriqué ; sans logo, le nom de l'organisme en texte.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type IdentiteOrganisme } from "../api";
import { liensLegaux } from "../liens-legaux";
import { cx, definirMarque } from "./base";

export const requeteIdentite = {
  queryKey: ["identite"],
  queryFn: () => api.get<IdentiteOrganisme>("/public/identite"),
  staleTime: 10 * 60_000,
} as const;

export function useIdentite() {
  const q = useQuery(requeteIdentite);
  useEffect(() => {
    if (q.data?.nom) definirMarque(q.data.nom);
  }, [q.data?.nom]);
  return q;
}

/** Logo (ou nom) de l'organisme. `taille` : hauteur du logo ; `sombre` : posé sur un fond foncé. */
export function Marque({
  className,
  sombre = false,
  taille = "h-7",
}: {
  className?: string;
  sombre?: boolean;
  taille?: string;
}) {
  const { data } = useIdentite();
  if (!data) return <span className={cx("inline-block w-32", taille, className)} aria-hidden />;
  if (data.logo)
    return (
      <img
        src={data.logo}
        alt={data.nom}
        className={cx(
          "w-auto max-w-full object-contain object-left",
          taille,
          sombre && "rounded-xs bg-carte/95 px-2 py-1",
          className,
        )}
      />
    );
  return (
    <span
      className={cx(
        "inline-block font-display text-xl font-semibold leading-none tracking-tight",
        sombre ? "text-sur-accent" : "text-encre",
        className,
      )}
    >
      {data.nom}
    </span>
  );
}

/** Liens légaux de l'organisme (mentions légales, CGU, confidentialité, site), s'il les a renseignés. */
export function LiensLegaux({ className }: { className?: string }) {
  const { data } = useIdentite();
  const liens = liensLegaux(data);
  if (!data || liens.length === 0) return null;
  return (
    <p className={cx("flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-encre-3", className)}>
      <span>{data.raison_sociale || data.nom}</span>
      {liens.map((l) => (
        <a
          key={l.href}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-6 items-center underline underline-offset-2 hover:text-encre"
        >
          {l.libelle}
          <span className="sr-only"> (nouvel onglet)</span>
        </a>
      ))}
    </p>
  );
}
